<template>
  <div class="food-court-cart mb-16">
    <NavHeader
      :title="navHeaderTitle"
      :subtitle="navHeaderSubtitle"
      :cta="{
        icon: 'mdi-delete-outline',
        onClick: () => (showDeleteModal = true),
      }"
      @onBackClick="goBack"
    />

    <LayoutCard v-if="initialLoad" :loading="true" rounded>
      <div class="d-flex justify-center align-center" style="height: 200px;">
        <v-progress-circular indeterminate />
      </div>
    </LayoutCard>

    <LayoutCard v-else-if="!hasCartItems" rounded>
      <StateScreen
        class="mt-8"
        :title="$t('screens.placeOrder.emptyStateTitle')"
        :subtitle="
          $t($store.getters.customizationEmptyCartScreenDescriptionText) ||
            $t('screens.placeOrder.emptyStateSubtitle')
        "
        image="https://cdn.greet.menu/choose_item_small_3.gif"
      />
    </LayoutCard>

    <div v-else>
      <MultiMerchantBanner
        v-if="!hasOrderFromMultipleRestaurants"
        @cta-click="goToFoodHall"
      />
      <div
        v-for="restaurantCart in restaurantsWithCarts"
        :key="`restaurant-${restaurantCart.restaurant_id}`"
        class="mb-4"
      >
        <LayoutCard
          :title="restaurantCartTitle(restaurantCart.restaurant_id)"
          rounded
        >
          <TransitionGroup name="opacity">
            <div
              v-for="(item, key) in restaurantCart.cart"
              :key="`cart-item-wrapper-${item.cart_item_id}`"
              class="cart-item-wrapper"
            >
              <CartItem
                :id="item.cart_item_id"
                :key="`cart-item-${item.cart_item_id}`"
                :item-key="key"
                :name="getItemDisplayName(item)"
                :quantity="item.quantity"
                :modifiers="transformItemOptions(item.options)"
                :price="getItemPrice(item)"
                :session="restaurantCart.session"
                @enablePayButton="onQuantityUpdated"
              />
            </div>
          </TransitionGroup>
        </LayoutCard>
      </div>
      <LayoutCard rounded>
        <PriceSummary :subtotal-amount="subtotalAmount" :show-total="false" />
      </LayoutCard>
    </div>

    <div v-if="hasCartItems" class="px-10 py-3">
      <CtaButton
        dark
        data-cy="proceed-to-checkout-food-court"
        :disabled="!hasCartItems || placeOrdersInProgress"
        :loading="placeOrdersInProgress"
        @click="proceedToCheckout"
      >
        {{ ctaLabel }}
      </CtaButton>
    </div>
    <Modal
      :is-open="showDeleteModal"
      :title="$t('screens.placeOrder.cartDeleteTitle')"
      :subtitle="$t('screens.placeOrder.cartDeleteSubtitle')"
      :cta-label="$t('screens.placeOrder.cartDeleteCta')"
      :cta-loading="clearingCart"
      @cta-click="clearCart"
      @close-click="showDeleteModal = false"
    />
  </div>
</template>

<script>
import NavHeader from '@/components/Navigation/NavHeader.vue';
import LayoutCard from '@/components/LayoutCard.vue';
import StateScreen from '@/components/StateScreen.vue';
import CtaButton from '@/components/Button.vue';
import CartItem from '@/components/Cart/CartItem.vue';
import PriceSummary from '@/components/common/PriceSummary.vue';
import Modal from '@/components/common/Modal.vue';
import MultiMerchantBanner from '@/components/Orders/MultiMerchantBanner.vue';

export default {
  name: 'FoodCourtCart',
  components: {
    NavHeader,
    LayoutCard,
    StateScreen,
    CtaButton,
    CartItem,
    PriceSummary,
    Modal,
    MultiMerchantBanner,
  },
  data() {
    return {
      initialLoad: false,
      showDeleteModal: false,
      clearingCart: false,
    };
  },
  computed: {
    foodCourtHash() {
      return this.$route.params.foodCourtHash;
    },
    foodCourtCarts() {
      return this.$store.getters.getFoodCourtCarts || [];
    },
    foodCourtCartsSessions() {
      return this.foodCourtCarts.map(cart => cart.session);
    },
    foodCourtRestaurants() {
      return this.$store.getters.getFoodCourtRestaurants || [];
    },
    isCartsLoading() {
      return this.$store.getters.getFoodCourtCartsLoading;
    },
    restaurantsWithCarts() {
      return this.$store.getters.getRestaurantsWithCarts || [];
    },
    hasCartItems() {
      return this.$store.getters.hasFoodCourtCartItems;
    },
    totalItemsCount() {
      return this.$store.getters.getTotalCartItemsCount;
    },
    subtotalAmount() {
      return this.$store.getters.getSubtotalAmountInFoodCourtCart;
    },
    placeOrdersInProgress() {
      return this.$store.getters.getPlaceOrdersInProgress;
    },
    placeOrdersIds() {
      return this.$store.getters.getPlaceOrdersIds;
    },
    additionalOrderFieldsRequired() {
      return this.$store.getters.getFoodCourtHasAdditionalOrderFields;
    },
    hasOrderFromMultipleRestaurants() {
      return this.foodCourtCarts.length > 1;
    },
    ctaLabel() {
      if (this.additionalOrderFieldsRequired) {
        return this.$t('screens.placeOrder.nextCtaLabel');
      }
      return this.$t('screens.placeOrder.continueToPaymentCta');
    },
    navHeaderTitle() {
      if (this.hasOrderFromMultipleRestaurants) {
        return this.$t('screens.foodCourtCart.headerTitlePlural');
      }

      return this.$t('screens.foodCourtCart.headerTitle');
    },
    navHeaderSubtitle() {
      if (this.hasOrderFromMultipleRestaurants) {
        return this.$t('screens.foodCourtCart.headerSubtitlePlural');
      }

      return '';
    },
  },
  async mounted() {
    this.initialLoad = true;
    try {
      await this.$store.dispatch('fetchFoodCourtCarts');
    } catch (error) {
      console.error('Error loading food court carts:', error);
    } finally {
      this.initialLoad = false;
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    restaurantCartTitle(restaurantId) {
      const restaurant = this.foodCourtRestaurants.find(
        r => r.id === restaurantId
      );
      return this.$t('screens.foodCourtCart.restaurantCartTitle', {
        restaurantName: restaurant?.name,
      });
    },
    getItemDisplayName(item) {
      let name = item.item_name;
      if (item.variation_name) {
        name += ` (${item.variation_name})`;
      }
      return name;
    },
    getItemPrice(item) {
      // Get base item price with priority: loyalty_price > discounted_price > price
      let basePrice = item.price;

      if (item.loyalty_price !== null && item.loyalty_price !== false) {
        basePrice = item.loyalty_price;
      } else if (
        item.discounted_price !== null &&
        item.discounted_price !== false
      ) {
        basePrice = item.discounted_price;
      }

      // Add modifier prices
      let totalPrice = basePrice;
      if (item.modifiers && item.modifiers.length > 0) {
        item.modifiers.forEach(modifier => {
          totalPrice += modifier.discounted_price || modifier.price || 0;
        });
      }

      return totalPrice;
    },

    transformItemOptions(options) {
      if (!options || options.length === 0) {
        return [];
      }

      return options.map(option => ({
        name: option.option_name,
        quantity: option.quantity || 1,
        markup: option.price || 0,
      }));
    },
    async proceedToCheckout() {
      if (this.additionalOrderFieldsRequired) {
        this.$router.push({
          name: 'FoodCourtExtraDetails',
          params: {
            foodCourtHash: this.$route.params.foodCourtHash,
          },
        });
        return;
      }

      try {
        await this.$store.dispatch('placeMultiMerchantOrders');
      } catch (error) {
        this.placingOrder = false;
        return;
      }

      this.$router.push({
        name: 'FoodCourtPayment',
        params: {
          foodCourtHash: this.$route.params.foodCourtHash,
          placedOrdersIds: this.placeOrdersIds.join(','),
        },
      });
    },
    async clearCart() {
      this.showDeleteModal = false;
      const sessions = this.foodCourtCartsSessions;
      this.clearingCart = true;
      await this.$store.dispatch('clearFoodCourtCart', { sessions });
      this.clearingCart = false;
    },
    goToFoodHall() {
      this.$router.push({
        name: 'FoodCourt',
        params: { foodCourtHash: this.foodCourtHash },
      });
    },
    // CartItem emits this once its update/remove request has completed, so the
    // refetch cannot race the write and revert the quantity.
    onQuantityUpdated() {
      this.$store.dispatch('fetchFoodCourtCarts');
    },
  },
};
</script>

<style lang="scss" scoped>
.cart-item-wrapper {
  display: flex;
  justify-content: space-between;
  &:not(:last-child) {
    margin-bottom: 20px;
  }
}
</style>
